'use client'

import React, { useState } from "react";
import styles from "./footer.module.scss";
import Link from 'next/link';

// Fallback local icon imports (used when CDN URLs are not available)
import instagramIcon from '@/assets/images/instagram.png';
import facebookIcon from '@/assets/images/facebook.png';
import pinterestIcon from '@/assets/images/pinterest.png';
import tiktokIcon from '@/assets/images/tiktok.png';
import youtubeIcon from '@/assets/images/youtube.png';
import heartIcon from '@/assets/images/heart.svg';
import companyIcon from '@/assets/images/company.svg';
import customerSupportIcon from '@/assets/images/customer_support.svg';
import shippingInfoIcon from '@/assets/images/truck.svg';

// Local icon fallback map keyed by lowercase name
const localIcons: Record<string, any> = {
    instagram: instagramIcon,
    facebook: facebookIcon,
    pinterest: pinterestIcon,
    tiktok: tiktokIcon,
    youtube: youtubeIcon,
    heart: heartIcon,
    company: companyIcon,
    'customer support': customerSupportIcon,
    'shipping info': shippingInfoIcon,
};

function getIconSrc(iconUrl?: string, fallbackKey?: string): string {
    if (iconUrl) return iconUrl;
    const key = fallbackKey?.toLowerCase() || '';
    return localIcons[key]?.src || '';
}

interface FooterComponentProps {
    brand?: any;
    footerJson?: any;
}

// Default footer data (hardcoded fallback when CMS JSON is not available)
const defaultFooterData = {
    socialMedia: {
        label: "Follow us",
        items: [
            { name: "Instagram", link: "https://www.instagram.com/nestingale/", iconSize: 40 },
            { name: "Facebook", link: "https://www.facebook.com/nestingale", iconSize: 25 },
            { name: "Pinterest", link: "https://www.pinterest.com/nestingale/", iconSize: 25 },
            { name: "TikTok", link: "https://www.tiktok.com/@nestingale.com", iconSize: 25 },
            { name: "YouTube", link: "https://www.youtube.com/@nestingale", iconSize: 25 },
        ]
    },
    columns: [
        {
            header: "Company",
            links: [
                { name: "About us", link: "/pages/about-us" },
                { name: "Blog", link: "/blogs/ideas" },
                { name: "How it works", link: "/pages/how-it-works" },
                { name: "Brand Partners", link: "/pages/brand-product-partners" },
            ]
        },
        {
            header: "Customer Support",
            links: [
                { name: "FAQs", link: "/pages/how-it-works" },
                { name: "Contact us", link: "/pages/contact" },
                { name: "Return policy", link: "/pages/return-policy" },
                { name: "Accessibility", link: "/pages/accessibility" },
            ]
        },
        {
            header: "Shipping Info",
            links: [
                { name: "Shipping Policy", link: "/pages/shipping-policy" },
            ]
        },
    ],
    bottomLinks: [
        { name: "Terms of Use", link: "/pages/terms-of-use" },
        { name: "Privacy Policy", link: "/pages/privacy-policy" },
        { name: "Cookie Settings", link: "/pages/cookie-policy" },
    ],
    copyright: "©{year} Nestingale | All Rights Reserved"
};

/** Renders a single social media icon link */
const SocialIcon: React.FC<{ item: any }> = ({ item }) => (
    <Link
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.socialIconLink}
    >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
            src={getIconSrc(item.iconUrl, item.icon || item.name)}
            alt={item.name}
            width={item.iconSize || 25}
            height={item.iconSize || 25}
        />
    </Link>
);

/** Renders the newsletter email signup input with arrow submit button */
const NewsletterEmailInput: React.FC<{ emailInput: any }> = ({ emailInput }) => {
    const [email, setEmail] = useState('');

    const handleSubmit = async () => {
        if (!email) return;
        try {
            await fetch('/showroom/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            setEmail('');
        } catch (err) {
            console.error('Newsletter signup failed:', err);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSubmit();
    };

    return (
        <div className={styles.newsletterInput}>
            <input
                type="email"
                placeholder={emailInput.placeholder || 'Enter your email'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Email for newsletter"
            />
            <div className={styles.newsletterInputDivider} />
            <button
                onClick={handleSubmit}
                aria-label="Subscribe to newsletter"
                className={styles.newsletterSubmitBtn}
            >
                <svg width="6" height="14" viewBox="0 0 6 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L5 7L1 13" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
};

const FooterComponent: React.FC<FooterComponentProps> = ({ brand, footerJson }) => {
    // Use CMS JSON if available, otherwise fall back to hardcoded defaults
    const data = (footerJson && Object.keys(footerJson).length > 0) ? footerJson : defaultFooterData;

    const { socialMedia, columns, bottomLinks, copyright, brand_description, socialMediaPosition } = data;

    // Replace {year} placeholder in copyright text
    const copyrightText = (copyright || defaultFooterData.copyright).replace('{year}', new Date().getFullYear().toString());

    // Determine if social media should render inline (inside footer) or above
    const isInlineSocial = socialMediaPosition === 'inline' || socialMedia?.position === 'inline';

    // Calculate total grid columns: brand_description adds 1, each column adds 1
    const totalColumns = (brand_description ? 1 : 0) + (columns?.length || 0);

    return (
        <>
            {/* Social Media Section - Only show ABOVE footer when NOT inline */}
            {socialMedia && !isInlineSocial && (
                <div className={styles.socialMediaSection}>
                    <div>
                        {socialMedia.label && (
                            <span className={styles.socialMediaSectionFollowUs}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={getIconSrc(socialMedia.iconUrl, 'heart')}
                                    alt="Follow us icon"
                                    width={15}
                                    height={15}
                                />
                                {socialMedia.label}
                            </span>
                        )}
                        {socialMedia.items?.map((item: any, index: number) => (
                            <SocialIcon key={index} item={item} />
                        ))}
                    </div>
                </div>
            )}

            {/* Footer Section */}
            <div
                className="footersection"
                style={data.style ? {
                    backgroundColor: data.style.background,
                    color: data.style.color,
                    padding: data.style.padding,
                    fontFamily: data.style.fontFamily,
                } : {}}
            >
                <div className="footer_container">
                    <div className={styles.footerContent}>
                        {/* Footer Navigation Grid */}
                        <div
                            className={styles.footerNav}
                            style={{ gridTemplateColumns: `repeat(${totalColumns}, 1fr)` }}
                        >
                            {/* Brand Description Column (e.g. GTR LEATHER) */}
                            {brand_description && (
                                <div className={styles.brandDescription}>
                                    <h3 className={styles.brandHeading}>
                                        {brand_description.heading}
                                    </h3>
                                    <p className={styles.brandText}>
                                        {brand_description.text}
                                    </p>
                                </div>
                            )}

                            {/* Dynamic Columns: links, newsletter, etc. */}
                            {columns?.map((column: any, colIndex: number) => {
                                const isNewsletter = column.type === 'newsletter' || !!column.emailInput;

                                return (
                                    <div
                                        key={colIndex}
                                        className={`${styles.footerColumn} ${isNewsletter ? styles.newsletterColumn : ''}`}
                                    >
                                        <h3>
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            {(column.iconUrl || localIcons[column.header?.toLowerCase()]) && (
                                                <img
                                                    src={getIconSrc(column.iconUrl, column.header)}
                                                    alt={`${column.header} Icon`}
                                                    width={20}
                                                    height={20}
                                                />
                                            )}
                                            {column.header}
                                        </h3>

                                        {/* Subtext (e.g. "Sign up for news & updates") */}
                                        {column.subtext && (
                                            <p className={styles.columnSubtext}>{column.subtext}</p>
                                        )}

                                        {/* Email Input for Newsletter */}
                                        {column.emailInput && (
                                            <NewsletterEmailInput emailInput={column.emailInput} />
                                        )}

                                        {/* Standard link list */}
                                        {column.links && column.links.length > 0 && (
                                            <ul>
                                                {column.links.map((link: any, linkIndex: number) => (
                                                    <li key={linkIndex}>
                                                        <Link
                                                            href={link.link}
                                                            {...(link.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                                        >
                                                            {link.name}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* Inline Follow Us + Social Icons (under newsletter) */}
                                        {isNewsletter && column.followUs && (
                                            <div className={styles.inlineSocialMedia}>
                                                {column.followUs.label && (
                                                    <span className={styles.followUsLabel}>
                                                        {column.followUs.label}
                                                    </span>
                                                )}
                                                <div className={styles.inlineSocialIcons}>
                                                    {column.followUs.items?.map((item: any, idx: number) => (
                                                        <SocialIcon key={idx} item={item} />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Bottom Links + Copyright */}
                        <div className={styles.footerBottom}>
                            {bottomLinks && bottomLinks.length > 0 && (
                                <div className={styles.footerBottomLinks}>
                                    {bottomLinks.map((link: any, index: number) => (
                                        <Link key={index} href={link.link}>
                                            {link.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                            <span className={styles.copyright}>
                                {copyrightText}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FooterComponent;
